import React, { useState, useRef, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    Box,
    DrawerFooter,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Icon,
    Input,
    InputGroup,
    InputLeftAddon,
    InputRightElement,
    Portal,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import Steps from 'components/ui/Steps';
import CustomDrawer from 'components/ui/Drawer';
import { Dropdown } from 'components/ui/Dropdown';
import { HSeparator } from 'components/separator/Separator';
import SelectCountry from 'components/ui/SelectCountry';
import { Country } from 'components/ui/countries_data';
import MiniCalendar from 'components/calendar/MiniCalendar';
import { patientService } from 'services/patientService';
import { antecedenteService } from 'services/antecedenteService';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { useCreateTarjetero } from 'hooks/useTarjetero';
import { AntecedentesHeredofamiliar, AntecedentesNoPatologico, AntecedentesPatologico, CreatePatientData, Genero } from 'types/models';
import Button from 'components/ui/Button';
import { Check } from 'lucide-react';
import { MdAdd, MdArrowBack, MdArrowForward, MdCalendarToday, MdDirectionsRun, MdEventSeat } from 'react-icons/md';

// ── Antecedentes ─────────────────────────────────────────────────────────────

type AnteTipo = 'patologico' | 'heredofamiliar' | 'no_patologico';

type AntecedenteItem = {
    tipo: AnteTipo;
    key: string;
    label: string;
    isCustom?: boolean;
};

type AlergiaItem = {
    descripcion: string;
    reaccion?: string;
};

const TIPO_LABELS: Record<AnteTipo, string> = {
    patologico: 'Patológico',
    heredofamiliar: 'Heredo-familiar',
    no_patologico: 'No patológico',
};

const TIPO_COLORS: Record<AnteTipo, string> = {
    patologico: 'red',
    heredofamiliar: 'blue',
    no_patologico: 'green',
};

const CONDICIONES_POR_TIPO: Record<AnteTipo, { key: string; label: string }[]> = {
    patologico: [
        { key: 'diabetesMellitus', label: 'Diabetes Mellitus' },
        { key: 'hipertensionArterial', label: 'Hipertensión Arterial' },
        { key: 'cardiopatias', label: 'Cardiopatías' },
        { key: 'enfermedadesRespiratorias', label: 'Enf. Respiratorias' },
        { key: 'enfermedadesRenales', label: 'Enf. Renales' },
        { key: 'cancer', label: 'Cáncer' },
        { key: 'tuberculosis', label: 'Tuberculosis' },
        { key: 'hepatitis', label: 'Hepatitis' },
        { key: 'vihSida', label: 'VIH/SIDA' },
        { key: 'covid19', label: 'COVID-19' },
        { key: 'epilepsia', label: 'Epilepsia' },
        { key: 'accidenteCerebrovascular', label: 'ACV' },
        { key: 'depresion', label: 'Depresión' },
        { key: 'ansiedad', label: 'Ansiedad' },
        { key: 'transfusiones', label: 'Transfusiones' },
    ],
    heredofamiliar: [
        { key: 'diabetes', label: 'Diabetes' },
        { key: 'hipertension', label: 'Hipertensión' },
        { key: 'cardiopatias', label: 'Cardiopatías' },
        { key: 'cancer', label: 'Cáncer' },
        { key: 'enfermedadesRespiratorias', label: 'Enf. Respiratorias' },
        { key: 'enfermedadesRenales', label: 'Enf. Renales' },
        { key: 'enfermedadesNeurologicas', label: 'Enf. Neurológicas' },
        { key: 'enfermedadesMentales', label: 'Enf. Mentales' },
    ],
    no_patologico: [
        { key: 'tabaquismo', label: 'Tabaquismo' },
        { key: 'alcoholismo', label: 'Alcoholismo' },
        { key: 'drogas', label: 'Drogas' },
        { key: 'cafe', label: 'Café' },
        { key: 'esquemaVacunacionCompleto', label: 'Vacunación completa' },
    ],
};

// ── Phone format segments by ISO (digits per group)
const PHONE_SEGMENTS: Record<string, number[]> = {
    EC: [3, 3, 4], AR: [2, 4, 4], US: [3, 3, 4], CA: [3, 3, 4],
    ES: [3, 2, 2, 2], MX: [2, 4, 4], CO: [3, 3, 4], PE: [3, 3, 3],
    CL: [1, 4, 4], BR: [2, 5, 4], VE: [3, 3, 4], BO: [2, 7],
    PY: [3, 6], UY: [2, 3, 4], FR: [2, 2, 2, 2, 2], DE: [3, 4, 4],
    IT: [3, 3, 4], GB: [4, 3, 4],
};

const applyPhoneFormat = (digits: string, iso: string): string => {
    const segs = PHONE_SEGMENTS[iso] ?? [3, 3, 4];
    const parts: string[] = [];
    let pos = 0;
    for (const len of segs) {
        if (pos >= digits.length) break;
        parts.push(digits.slice(pos, pos + len));
        pos += len;
    }
    if (pos < digits.length) parts.push(digits.slice(pos));
    return parts.join(' ');
};

// ── PhoneInput: flag selector + code badge + formatted number ──────────────
interface PhoneInputProps {
    value: string;
    onChange: (val: string) => void;
    isInvalid?: boolean;
    dropdownOffset?: number;
}

const PhoneInput: React.FC<PhoneInputProps> = ({ value, onChange, isInvalid, dropdownOffset}) => {
    const [country, setCountry] = useState<Country | null>(null);
    const addonBg = useColorModeValue('gray.50', 'gray.700');
    const addonColor = useColorModeValue('gray.600', 'gray.300');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    dropdownOffset =  8.5;

    // Strip country code prefix to get editable number part
    const safeValue = value ?? '';
    const numberOnly = country && safeValue.startsWith(country.code)
        ? safeValue.slice(country.code.length).trim()
        : safeValue;

    const handleSelect = (c: Country) => {
        setCountry(c);
        onChange('');
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, '');
        const formatted = country ? applyPhoneFormat(raw, country.iso) : raw;
        onChange(country ? `${country.code} ${formatted}`.trim() : formatted);
    };

    const placeholder = country
        ? applyPhoneFormat('0'.repeat(PHONE_SEGMENTS[country.iso]?.reduce((a, b) => a + b, 0) ?? 10), country.iso)
        : 'Número';

    return (
        <Grid templateColumns="60px 1fr" gap="8px" alignItems="flex-start">
            <SelectCountry
                size="sm"
                flagOnly
                triggerWidth="60px"
                dropdownWidth="320px"
                dropdownPosition="bottom"
                dropdownOffset={dropdownOffset}
                borderRadius="7px"
                onSelect={handleSelect}
            />
            <InputGroup size="sm">
                <InputLeftAddon
                    px="10px"
                    fontSize="xs"
                    fontWeight="600"
                    bg={addonBg}
                    color={addonColor}
                    borderColor={borderColor}
                    borderRadius="7px 0 0 7px"
                    minW="52px"
                    justifyContent="center"
                >
                    {country?.code ?? '+'}
                </InputLeftAddon>
                <Input
                    borderRadius="0 7px 7px 0"
                    placeholder={placeholder}
                    value={numberOnly}
                    onChange={handleNumberChange}
                    isInvalid={isInvalid}
                />
            </InputGroup>
        </Grid>
    );
};

// ── DatePickerInput — teclado (dd/mm/aaaa) + icono para abrir calendario ──
interface DatePickerInputProps {
    value: string;
    onChange: (val: string) => void;
    isInvalid?: boolean;
}

const DatePickerInput: React.FC<DatePickerInputProps> = ({ value, onChange, isInvalid }) => {
    const [showCal, setShowCal]     = useState(false);
    const [rect, setRect]           = useState<DOMRect | null>(null);
    const [inputText, setInputText] = useState('');
    const triggerRef  = useRef<HTMLDivElement>(null);
    const calRef      = useRef<HTMLDivElement>(null);
    const justClosed  = useRef(false);
    const inputBorder      = useColorModeValue('gray.200', 'gray.600');
    const inputBorderFocus = useColorModeValue('blue.400', 'blue.300');
    const iconColor        = useColorModeValue('gray.400', 'gray.500');

    // Sync display text when value resets externally
    useEffect(() => {
        if (!value) { setInputText(''); return; }
        const d = new Date(value + 'T00:00:00');
        if (!isNaN(d.getTime())) {
            setInputText(d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }));
        }
    }, [value]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const t = e.target as Node;
            if (!triggerRef.current?.contains(t) && !calRef.current?.contains(t)) {
                setShowCal(false);
                justClosed.current = true;
                setTimeout(() => { justClosed.current = false; }, 0);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const openCal = () => {
        if (justClosed.current) return;
        if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
        setShowCal(true);
    };

    const handleKeyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
        let formatted = digits;
        if (digits.length > 2) formatted = digits.slice(0, 2) + '/' + digits.slice(2);
        if (digits.length > 4) formatted = formatted.slice(0, 5) + '/' + formatted.slice(5);
        setInputText(formatted);

        if (digits.length === 8) {
            const day   = parseInt(digits.slice(0, 2), 10);
            const month = parseInt(digits.slice(2, 4), 10) - 1;
            const year  = parseInt(digits.slice(4, 8), 10);
            const d = new Date(year, month, day);
            if (d.getFullYear() === year && d.getMonth() === month && d.getDate() === day) {
                onChange(d.toISOString().split('T')[0]);
                return;
            }
        }
        onChange('');
    };

    const dateVal = value ? new Date(value + 'T00:00:00') : null;

    return (
        <Box ref={triggerRef} position="relative">
            <InputGroup size="sm">
                <Input
                    borderRadius="7px"
                    placeholder="dd/mm/aaaa"
                    value={inputText}
                    onChange={handleKeyInput}
                    isInvalid={isInvalid}
                    borderColor={showCal ? inputBorderFocus : inputBorder}
                    _hover={{ borderColor: inputBorderFocus }}
                    pr="36px"
                />
                <InputRightElement width="34px">
                    <Box
                        as="button"
                        type="button"
                        onClick={openCal}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        color={iconColor}
                        _hover={{ color: inputBorderFocus }}
                        transition="color 0.15s"
                    >
                        <Icon as={MdCalendarToday} w="14px" h="14px" />
                    </Box>
                </InputRightElement>
            </InputGroup>
            {showCal && rect && (
                <Portal>
                    <Box
                        ref={calRef}
                        position="fixed"
                        top={`${rect.bottom + 6}px`}
                        left={`${rect.left}px`}
                        zIndex={9999}
                        boxShadow="0 8px 24px rgba(0,0,0,0.14)"
                        borderRadius="12px"
                        overflow="hidden"
                    >
                        <MiniCalendar
                            value={dateVal}
                            onChange={(val) => {
                                if (val instanceof Date) {
                                    onChange(val.toISOString().split('T')[0]);
                                    setInputText(val.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }));
                                    setShowCal(false);
                                }
                            }}
                            size='md'
                            drillable
                        />
                    </Box>
                </Portal>
            )}
        </Box>
    );
};

interface NewPatientFormData {
    nombre: string;
    apellido: string;
    tipoIdentificacion: string;
    cedula: string;
    genero: string;
    generoPersonalizado?: string;
    fechaNacimiento: string;
    telefono: string;
    telefonoEmergencia?: string;
    email?: string;
    direccion?: string;
    ciudad?: string;
    provincia?: string;
    codigoPostal?: string;
    ocupacion?: string;
    estadoCivil?: string;
}

const schema = yup.object({
    nombre: yup.string().min(2, 'Mínimo 2 caracteres').required('Requerido'),
    apellido: yup.string().min(2, 'Mínimo 2 caracteres').required('Requerido'),
    tipoIdentificacion: yup.string().required('Requerido'),
    cedula: yup.string().min(5, 'Mínimo 5 caracteres').matches(/^[\d\-A-Z]+$/, 'Solo números, guiones y letras').required('Requerido'),
    genero: yup.string().required('Requerido'),
    generoPersonalizado: yup.string().when('genero', {
        is: 'otro',
        then: (s) => s.required('Especifica el género'),
        otherwise: (s) => s.optional(),
    }),
    fechaNacimiento: yup.string().required('Requerido'),
    telefono: yup
        .string()
        .matches(/^[+\d\s]{7,20}$/, 'Formato inválido')
        .required('Requerido'),
    telefonoEmergencia: yup.string().optional(),
    email: yup.string().email('Correo inválido').optional(),
    direccion: yup.string().optional(),
    ciudad: yup.string().optional(),
    provincia: yup.string().optional(),
    codigoPostal: yup
        .string()
        .matches(/^\d{0,10}$/, 'Solo números')
        .optional(),
    ocupacion: yup.string().optional(),
    estadoCivil: yup.string().optional(),
});

const ID_TYPE_ITEMS = [
    { label: 'Cédula de Identidad', value: 'cedula'     },
    { label: 'Pasaporte',           value: 'pasaporte'  },
    { label: 'RUC',                 value: 'ruc'        },
    { label: 'DNI',                 value: 'dni'        },
    { label: 'Tarjeta de Residencia', value: 'residencia' },
];

const GENERO_ITEMS = [
    { label: 'Masculino',  value: 'masculino'  },
    { label: 'Femenino',   value: 'femenino'   },
    { label: 'No Binario', value: 'no-binario' },
    { label: 'Otro',       value: 'otro'       },
];

const ESTADO_CIVIL_ITEMS = [
    { label: 'Soltero/a',    value: 'soltero'    },
    { label: 'Casado/a',     value: 'casado'     },
    { label: 'Divorciado/a', value: 'divorciado' },
    { label: 'Viudo/a',      value: 'viudo'      },
    { label: 'Unión Libre',  value: 'union-libre'},
];

const WIZARD_STEPS = [
    { id: 'datos',        title: 'Datos del Paciente',    description: 'Información básica'  },
    { id: 'antecedentes', title: 'Antecedentes y Alergias', description: 'Historial clínico' },
];

// ─────────────────────────────────────────────────────────────────────────────

interface NewPatientDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated?: () => void;
}

export const NewPatientDrawer: React.FC<NewPatientDrawerProps> = ({ isOpen, onClose, onCreated }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const toast = useToast();
    const { data: currentUser } = useCurrentDbUser();
    const createTarjetero = useCreateTarjetero();
    const labelColor = useColorModeValue('gray.700', 'gray.200');
    const sectionColor = useColorModeValue('gray.500', 'gray.400');
    const inputBg = useColorModeValue('gray.50', 'navy.700');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    // ── Antecedentes state ────────────────────────────────────────────────────
    const [antecedentes, setAntecedentes] = useState<AntecedenteItem[]>([]);
    const [showAnteForm, setShowAnteForm] = useState(false);
    const [anteType, setAnteType] = useState<AnteTipo>('patologico');
    const [anteCondicion, setAnteCondicion] = useState('');
    const [anteCondicionManual, setAnteCondicionManual] = useState('');

    // ── Actividad física state ────────────────────────────────────────────────
    const [actividadNivel, setActividadNivel] = useState<'sedentario' | 'activo' | null>(null);
    const [actividadTipo, setActividadTipo] = useState('');
    const [actividadFrecuencia, setActividadFrecuencia] = useState('');

    // ── Alergias state ────────────────────────────────────────────────────────
    const [alergias, setAlergias] = useState<AlergiaItem[]>([]);
    const [alergiaDesc, setAlergiaDesc] = useState('');
    const [alergiaReaccion, setAlergiaReaccion] = useState('');

    const resetLocalState = () => {
        setCurrentStep(0);
        setAntecedentes([]);
        setAlergias([]);
        setShowAnteForm(false);
        setAnteType('patologico');
        setAnteCondicion('');
        setAnteCondicionManual('');
        setAlergiaDesc('');
        setAlergiaReaccion('');
        setActividadNivel(null);
        setActividadTipo('');
        setActividadFrecuencia('');
    };

    const addAntecedente = () => {
        if (anteCondicion === '__custom__') {
            if (!anteCondicionManual.trim()) return;
            setAntecedentes(prev => [...prev, {
                tipo: anteType,
                key: '__custom__',
                label: anteCondicionManual.trim(),
                isCustom: true,
            }]);
        } else {
            if (!anteCondicion) return;
            const found = CONDICIONES_POR_TIPO[anteType].find(c => c.key === anteCondicion);
            if (!found) return;
            if (antecedentes.some(a => a.tipo === anteType && a.key === anteCondicion)) return;
            setAntecedentes(prev => [...prev, { tipo: anteType, key: anteCondicion, label: found.label }]);
        }
        setAnteCondicion('');
        setAnteCondicionManual('');
        setShowAnteForm(false);
    };

    const removeAntecedente = (idx: number) =>
        setAntecedentes(prev => prev.filter((_, i) => i !== idx));

    const addAlergia = () => {
        if (!alergiaDesc.trim()) return;
        setAlergias(prev => [...prev, {
            descripcion: alergiaDesc.trim(),
            reaccion: alergiaReaccion.trim() || undefined,
        }]);
        setAlergiaDesc('');
        setAlergiaReaccion('');
    };

    const removeAlergia = (idx: number) =>
        setAlergias(prev => prev.filter((_, i) => i !== idx));

    const handleClose = () => {
        reset();
        resetLocalState();
        onClose();
    };

    const {
        register,
        handleSubmit,
        control,
        watch,
        reset,
        trigger,
        formState: { errors },
    } = useForm<NewPatientFormData>({
        resolver: yupResolver(schema) as any,
        defaultValues: { genero: '', telefono: '', telefonoEmergencia: '', email: '' },
    });

    const handleNext = async () => {
        const valid = await trigger();
        if (valid) setCurrentStep(1);
    };

    const generoValue = watch('genero');

    // Strip country code prefix (+XXX) and spaces — backend expects local digits only
    const stripCountryCode = (phone: string) =>
        phone.replace(/^\+\d+\s*/, '').replace(/\s/g, '') || undefined;

    const onSubmit = async (data: NewPatientFormData) => {
        setIsSaving(true);
        try {
            const payload: CreatePatientData = {
                cedula: data.cedula,
                nombres: data.nombre,
                apellidos: data.apellido,
                fechaNacimiento: data.fechaNacimiento,
                genero: (data.genero === 'no-binario' ? 'otro' : data.genero) as Genero,
                telefono: data.telefono ? stripCountryCode(data.telefono) : undefined,
                telefonoEmergencia: data.telefonoEmergencia ? stripCountryCode(data.telefonoEmergencia) : undefined,
                email: data.email || undefined,
                direccion: data.direccion || undefined,
                ciudad: data.ciudad || undefined,
                provincia: data.provincia || undefined,
                codigoPostal: data.codigoPostal || undefined,
            };
            const patient = await patientService.create(payload);
            await createTarjetero.mutateAsync({
                patientId: patient.id,
                payload: { medicoResponsableId: currentUser?.id },
            });

            // Patch antecedentes (parallel, non-fatal)
            const patches: Promise<any>[] = [];
            const heredoItems = antecedentes.filter(a => a.tipo === 'heredofamiliar');
            const patItems    = antecedentes.filter(a => a.tipo === 'patologico');
            const noPatItems  = antecedentes.filter(a => a.tipo === 'no_patologico');

            if (heredoItems.length > 0) {
                const p: Record<string, any> = {};
                heredoItems.filter(i => !i.isCustom).forEach(i => { p[i.key] = true; });
                const heredoCustom = heredoItems.filter(i => i.isCustom);
                if (heredoCustom.length > 0) p.otros = heredoCustom.map(i => ({ enfermedad: i.label }));
                patches.push(antecedenteService.updateHeredofamiliares(patient.id, p as Partial<AntecedentesHeredofamiliar>));
            }
            if (patItems.length > 0 || alergias.length > 0) {
                const p: Record<string, any> = {};
                patItems.filter(i => !i.isCustom).forEach(i => { p[i.key] = true; });
                const patCustom = patItems.filter(i => i.isCustom);
                if (patCustom.length > 0) p.otros = patCustom.map(i => ({ descripcion: i.label }));
                if (alergias.length > 0) p.alergiasMedicamentos = alergias.map(a => ({ descripcion: a.descripcion, reaccion: a.reaccion }));
                patches.push(antecedenteService.updatePatologicos(patient.id, p as Partial<AntecedentesPatologico>));
            }
            if (noPatItems.length > 0 || actividadNivel !== null) {
                const p: Record<string, any> = {};
                noPatItems.filter(i => !i.isCustom).forEach(i => { p[i.key] = true; });
                const noPatCustom = noPatItems.filter(i => i.isCustom);
                if (noPatCustom.length > 0) p.otrosHabitos = noPatCustom.map(i => i.label).join(', ');
                if (actividadNivel === 'activo') {
                    p.actividadFisica = true;
                    if (actividadTipo) p.actividadFisicaTipo = actividadTipo;
                    if (actividadFrecuencia) p.actividadFisicaFrecuencia = actividadFrecuencia;
                } else if (actividadNivel === 'sedentario') {
                    p.actividadFisica = false;
                }
                patches.push(antecedenteService.updateNoPatologicos(patient.id, p as Partial<AntecedentesNoPatologico>));
            }

            let anteOk = true;
            if (patches.length > 0) {
                const results = await Promise.allSettled(patches);
                anteOk = results.every(r => r.status === 'fulfilled');
            }

            if (anteOk) {
                toast({ title: 'Paciente creado', status: 'success', duration: 3000, isClosable: true });
            } else {
                toast({
                    title: 'Paciente creado',
                    description: 'Algunos antecedentes no pudieron guardarse. Complételos en la ficha del paciente.',
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                });
            }

            reset();
            resetLocalState();
            onClose();
            onCreated?.();
        } catch (e: any) {
            toast({
                title: 'Error al crear paciente',
                description: e?.response?.data?.message ?? 'Intente nuevamente',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const optColor = useColorModeValue('gray.400', 'gray.500');
    const cardIconBg = useColorModeValue('gray.100', 'navy.700');

    const drawerFooter = (
        <DrawerFooter borderTopWidth="1px" borderTopColor={borderColor} gap="10px">
            {currentStep === 0 ? (
                <>
                    <Button variant="ghost" onClick={handleClose} isDisabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button variant="brand" rightIcon={<Icon as={MdArrowForward} />} onClick={handleNext}>
                        Siguiente
                    </Button>
                </>
            ) : (
                <>
                    <Button
                        variant="ghost"
                        leftIcon={<Icon as={MdArrowBack} />}
                        onClick={() => setCurrentStep(0)}
                        isDisabled={isSaving}
                    >
                        Volver
                    </Button>
                    <Button variant="brand" onClick={handleSubmit(onSubmit)} isLoading={isSaving}>
                        Guardar Paciente
                    </Button>
                </>
            )}
        </DrawerFooter>
    );

    const SectionHeader = ({ label }: { label: string }) => (
        <Text fontSize="xs" fontWeight="700" color={sectionColor} textTransform="uppercase" letterSpacing="0.08em">
            {label}
        </Text>
    );

    const Opt = () => (
        <Text as="span" fontSize="xs" fontWeight="400" color={optColor} ms="6px">
            Opcional
        </Text>
    );

    return (
        <CustomDrawer
            isOpen={isOpen}
            onClose={handleClose}
            title={
                <Flex align="center" gap="10px">
                    <Icon viewBox="0 0 24 24" w="20px" h="20px" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                    </Icon>
                    Nuevo Paciente
                </Flex>
            }
            size="md"
            placement="right"
            borderRadius="10px"
            borderRadiusSides="left"
            width="700px"
            layout="form"
            contentPaddingX="36px"
            contentPaddingY="28px"
            footer={drawerFooter}
        >
            <Steps
                steps={WIZARD_STEPS}
                activeStep={currentStep}
                onStepChange={(i) => { if (i < currentStep) setCurrentStep(i); }}
                labelPlacement="bottom"
                size="md"
                colorScheme="brand"
            />

            <HSeparator my="24px" />

            {currentStep === 0 && (
            <Flex direction="column" gap="16px">

            {/* ─── Sección 1: Información General ─── */}
            <SectionHeader label="Información General" />

            <Grid templateColumns="1fr 1fr" gap="16px">
                <FormControl isRequired isInvalid={!!errors.nombre}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>Nombre</FormLabel>
                    <Input size="sm" borderRadius="7px" placeholder="Ej. Juan" {...register('nombre')} />
                    <FormErrorMessage fontSize="xs">{errors.nombre?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.apellido}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>Apellido</FormLabel>
                    <Input size="sm" borderRadius="7px" placeholder="Ej. Pérez" {...register('apellido')} />
                    <FormErrorMessage fontSize="xs">{errors.apellido?.message}</FormErrorMessage>
                </FormControl>
            </Grid>

            {/* Identificación: Tipo (120px) | Número (1fr) | Género (1fr) */}
            <Grid templateColumns="120px 1fr 1fr" gap="12px" alignItems="flex-start">
                <FormControl isRequired isInvalid={!!errors.tipoIdentificacion}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>Identificación</FormLabel>
                    <Controller
                        name="tipoIdentificacion"
                        control={control}
                        render={({ field }) => (
                            <Dropdown
                                items={ID_TYPE_ITEMS}
                                label={ID_TYPE_ITEMS.find(t => t.value === field.value)?.label || 'Tipo'}
                                onSelect={(val) => field.onChange(val)}
                                borderRadius="7px"
                                size="sm"
                                w="full"
                                fontSize="sm"
                                fontWeight="400"
                                variant="outline"
                                showIcon
                                menuWidth="158px"
                                itemPx="12px"
                                itemPy="8px"
                            />
                        )}
                    />
                    <FormErrorMessage fontSize="xs">{errors.tipoIdentificacion?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.cedula}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>&nbsp;</FormLabel>
                    <Input
                        size="sm"
                        borderRadius="7px"
                        placeholder="Número de identificación"
                        {...register('cedula', {
                            setValueAs: (v: string) => v.toUpperCase().replace(/[^0-9\-A-Z]/g, ''),
                        })}
                    />
                    <FormErrorMessage fontSize="xs">{errors.cedula?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.genero}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>Género</FormLabel>
                    <Controller
                        name="genero"
                        control={control}
                        render={({ field }) => (
                            <Dropdown
                                items={GENERO_ITEMS}
                                label={GENERO_ITEMS.find(g => g.value === field.value)?.label || 'Seleccionar'}
                                onSelect={(val) => field.onChange(val)}
                                borderRadius="7px"
                                size="sm"
                                w="full"
                                fontSize="sm"
                                fontWeight="400"
                                variant="outline"
                                showIcon
                                menuWidth="150px"
                                itemPx="12px"
                                itemPy="8px"
                            />
                        )}
                    />
                    <FormErrorMessage fontSize="xs">{errors.genero?.message}</FormErrorMessage>
                </FormControl>
            </Grid>

            <FormControl isRequired isInvalid={!!errors.fechaNacimiento}>
                <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>Fecha de Nacimiento</FormLabel>
                <Controller
                    name="fechaNacimiento"
                    control={control}
                    render={({ field }) => (
                        <DatePickerInput
                            value={field.value}
                            onChange={field.onChange}
                            isInvalid={!!errors.fechaNacimiento}
                        />
                    )}
                />
                <FormErrorMessage fontSize="xs">{errors.fechaNacimiento?.message}</FormErrorMessage>
            </FormControl>

            {generoValue === 'otro' && (
                <FormControl isRequired isInvalid={!!errors.generoPersonalizado}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>Especifica el género</FormLabel>
                    <Input size="sm" borderRadius="7px" placeholder="Ingresa tu género" {...register('generoPersonalizado')} />
                    <FormErrorMessage fontSize="xs">{errors.generoPersonalizado?.message}</FormErrorMessage>
                </FormControl>
            )}

            {/* ─── Sección 2: Información de Contacto ─── */}
            <HSeparator />
            <SectionHeader label="Información de Contacto" />

            <Grid templateColumns="1fr 1fr" gap="16px">
                <FormControl isRequired isInvalid={!!errors.telefono}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>Teléfono</FormLabel>
                    <Controller
                        name="telefono"
                        control={control}
                        render={({ field }) => (
                            <PhoneInput
                                value={field.value}
                                onChange={field.onChange}
                                isInvalid={!!errors.telefono}
                                dropdownOffset={30}
                            />
                        )}
                    />
                    <FormErrorMessage fontSize="xs">{errors.telefono?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.telefonoEmergencia}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                        Teléfono de Emergencia <Opt />
                    </FormLabel>
                    <Controller
                        name="telefonoEmergencia"
                        control={control}
                        render={({ field }) => (
                            <PhoneInput
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                isInvalid={!!errors.telefonoEmergencia}
                                dropdownOffset={30}
                            />
                        )}
                    />
                    <FormErrorMessage fontSize="xs">{errors.telefonoEmergencia?.message}</FormErrorMessage>
                </FormControl>
            </Grid>

            <FormControl isInvalid={!!errors.email}>
                <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                    Correo Electrónico <Opt />
                </FormLabel>
                <Input size="sm" borderRadius="7px" type="email" placeholder="correo@ejemplo.com" {...register('email')} />
                <FormErrorMessage fontSize="xs">{errors.email?.message}</FormErrorMessage>
            </FormControl>

            {/* Dirección (2fr) | Ciudad (1fr) */}
            <Grid templateColumns="2fr 1fr" gap="16px">
                <FormControl isInvalid={!!errors.direccion}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                        Dirección <Opt />
                    </FormLabel>
                    <Input size="sm" borderRadius="7px" placeholder="Av. Corrientes 1234" {...register('direccion')} />
                    <FormErrorMessage fontSize="xs">{errors.direccion?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.ciudad}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                        Ciudad <Opt />
                    </FormLabel>
                    <Input size="sm" borderRadius="7px" placeholder="Buenos Aires" {...register('ciudad')} />
                    <FormErrorMessage fontSize="xs">{errors.ciudad?.message}</FormErrorMessage>
                </FormControl>
            </Grid>

            {/* Provincia (1fr) | C.P. (120px) */}
            <Grid templateColumns="1fr 120px" gap="16px">
                <FormControl isInvalid={!!errors.provincia}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                        Provincia <Opt />
                    </FormLabel>
                    <Input size="sm" borderRadius="7px" placeholder="Buenos Aires" {...register('provincia')} />
                    <FormErrorMessage fontSize="xs">{errors.provincia?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.codigoPostal}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                        C.P. <Opt />
                    </FormLabel>
                    <Input size="sm" borderRadius="7px" placeholder="1043" {...register('codigoPostal')} />
                    <FormErrorMessage fontSize="xs">{errors.codigoPostal?.message}</FormErrorMessage>
                </FormControl>
            </Grid>

            {/* ─── Sección 3: Información Adicional ─── */}
            <HSeparator />
            <SectionHeader label="Información Adicional" />

            <Grid templateColumns="1fr 1fr" gap="16px">
                <FormControl isInvalid={!!errors.ocupacion}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                        Ocupación <Opt />
                    </FormLabel>
                    <Input size="sm" borderRadius="7px" placeholder="Ej. Docente" {...register('ocupacion')} />
                    <FormErrorMessage fontSize="xs">{errors.ocupacion?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.estadoCivil}>
                    <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                        Estado Civil <Opt />
                    </FormLabel>
                    <Controller
                        name="estadoCivil"
                        control={control}
                        render={({ field }) => (
                            <Dropdown
                                items={ESTADO_CIVIL_ITEMS}
                                label={ESTADO_CIVIL_ITEMS.find(e => e.value === field.value)?.label || 'Seleccionar'}
                                onSelect={(val) => field.onChange(val)}
                                borderRadius="7px"
                                size="sm"
                                w="full"
                                fontSize="sm"
                                fontWeight="400"
                                variant="outline"
                                showIcon
                                menuWidth="100%"
                                itemPx="12px"
                                itemPy="8px"
                            />
                        )}
                    />
                    <FormErrorMessage fontSize="xs">{errors.estadoCivil?.message}</FormErrorMessage>
                </FormControl>
            </Grid>

            </Flex>
            )}

            {currentStep === 1 && (
            <Flex direction="column" gap="16px">

            {/* ─── Actividad Física ─── */}
            <SectionHeader label="Actividad Física" />

            <Grid templateColumns="1fr 1fr" gap="10px">
                {(
                    [
                        { value: 'sedentario', icon: MdEventSeat,     label: 'Sedentario',    desc: 'Sin actividad física regular', color: 'gray' },
                        { value: 'activo',     icon: MdDirectionsRun, label: 'Activo',        desc: 'Realiza actividad física regular', color: 'green' },
                    ] as const
                ).map(({ value, icon, label, desc, color }) => {
                    const selected = actividadNivel === value;
                    return (
                        <Box
                            key={value}
                            as="button"
                            type="button"
                            onClick={() => setActividadNivel(selected ? null : value)}
                            border="2px solid"
                            borderColor={selected ? `${color}.400` : borderColor}
                            borderRadius="12px"
                            p="14px"
                            textAlign="center"
                            bg={selected ? `${color}.50` : 'transparent'}
                            transition="all 0.15s"
                            _hover={{ borderColor: `${color}.300`, bg: `${color}.50` }}
                            cursor="pointer"
                        >
                            <Flex direction="column" align="center" gap="6px">
                                <Flex
                                    w="36px" h="36px" borderRadius="10px"
                                    bg={selected ? `${color}.100` : cardIconBg}
                                    align="center" justify="center"
                                >
                                    <Icon as={icon} color={selected ? `${color}.500` : 'gray.400'} w="18px" h="18px" />
                                </Flex>
                                <Text fontSize="sm" fontWeight="700" color={selected ? `${color}.600` : labelColor}>
                                    {label}
                                </Text>
                                <Text fontSize="xs" color={sectionColor}>
                                    {desc}
                                </Text>
                            </Flex>
                        </Box>
                    );
                })}
            </Grid>

            {actividadNivel === 'activo' && (
                <Grid templateColumns="1fr 1fr" gap="10px">
                    <FormControl>
                        <FormLabel fontSize="xs" fontWeight="600" color={labelColor} mb="6px">
                            Tipo de actividad <Text as="span" fontSize="xs" color={optColor}>Opcional</Text>
                        </FormLabel>
                        <Input
                            size="sm" borderRadius="7px"
                            placeholder="Natación, ciclismo, fútbol..."
                            value={actividadTipo}
                            onChange={e => setActividadTipo(e.target.value)}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs" fontWeight="600" color={labelColor} mb="6px">
                            Frecuencia <Text as="span" fontSize="xs" color={optColor}>Opcional</Text>
                        </FormLabel>
                        <Dropdown
                            items={[
                                { label: 'Diaria',            value: 'diaria'          },
                                { label: '5-6 días/semana',   value: '5-6 días/semana' },
                                { label: '3-4 días/semana',   value: '3-4 días/semana' },
                                { label: '1-2 días/semana',   value: '1-2 días/semana' },
                                { label: 'Ocasional',         value: 'ocasional'       },
                            ]}
                            label={actividadFrecuencia || 'Seleccionar...'}
                            onSelect={(val) => setActividadFrecuencia(String(val))}
                            size="sm" w="full" variant="outline" showIcon borderRadius="7px"
                            fontSize="sm" fontWeight="400" menuWidth="200px" itemPx="12px" itemPy="8px"
                        />
                    </FormControl>
                </Grid>
            )}

            <HSeparator />
            <SectionHeader label="Antecedentes" />

            {antecedentes.length > 0 && (
                <Flex flexWrap="wrap" gap="6px">
                    {antecedentes.map((a, idx) => (
                        <Tag key={idx} size="sm" colorScheme={TIPO_COLORS[a.tipo]} borderRadius="full">
                            <TagLabel>{a.label} · {TIPO_LABELS[a.tipo]}</TagLabel>
                            <TagCloseButton onClick={() => removeAntecedente(idx)} />
                        </Tag>
                    ))}
                </Flex>
            )}

            {showAnteForm ? (
                <Grid templateColumns="1fr 1fr auto" gap="8px" alignItems="flex-end">
                    <FormControl>
                        <FormLabel fontSize="xs" fontWeight="600" color={labelColor}>Tipo</FormLabel>
                        <Dropdown
                            items={[
                                { label: 'Patológico',      value: 'patologico'     },
                                { label: 'Heredo-familiar', value: 'heredofamiliar' },
                                { label: 'No patológico',   value: 'no_patologico'  },
                            ]}
                            label={TIPO_LABELS[anteType]}
                            onSelect={(val) => { setAnteType(val as AnteTipo); setAnteCondicion(''); }}
                            borderRadius="7px"
                            size="sm"
                            w="full"
                            fontSize="sm"
                            fontWeight="400"
                            variant="outline"
                            showIcon
                            menuWidth="200px"
                            itemPx="12px"
                            itemPy="8px"
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs" fontWeight="600" color={labelColor}>Condición</FormLabel>
                        {anteCondicion === '__custom__' ? (
                            <Input
                                size="sm"
                                borderRadius="7px"
                                placeholder="Escribir condición..."
                                value={anteCondicionManual}
                                onChange={e => setAnteCondicionManual(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAntecedente(); } }}
                                autoFocus
                            />
                        ) : (
                            <Dropdown
                                items={[
                                    ...CONDICIONES_POR_TIPO[anteType]
                                        .filter(c => !antecedentes.some(a => a.tipo === anteType && a.key === c.key))
                                        .map(c => ({ label: c.label, value: c.key })),
                                    { label: 'Otra (manual)...', value: '__custom__' },
                                ]}
                                label={
                                    anteCondicion
                                        ? (CONDICIONES_POR_TIPO[anteType].find(c => c.key === anteCondicion)?.label ?? 'Seleccionar...')
                                        : 'Seleccionar...'
                                }
                                onSelect={(val) => setAnteCondicion(String(val))}
                                borderRadius="7px"
                                size="sm"
                                w="full"
                                fontSize="sm"
                                fontWeight="400"
                                variant="outline"
                                showIcon
                                menuWidth="220px"
                                listMaxH="200px"
                                itemPx="12px"
                                itemPy="8px"
                            />
                        )}
                    </FormControl>
                    <Flex gap="6px" alignSelf="flex-end">
                        <Button
                            size="sm"
                            variant="brand"
                            onClick={addAntecedente}
                            isDisabled={anteCondicion === '__custom__' ? !anteCondicionManual.trim() : !anteCondicion}
                        >
                            <Icon as={MdAdd} />
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                                if (anteCondicion === '__custom__') {
                                    setAnteCondicion('');
                                    setAnteCondicionManual('');
                                } else {
                                    setShowAnteForm(false);
                                }
                            }}
                        >✕</Button>
                    </Flex>
                </Grid>
            ) : (
                <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<Icon as={MdAdd} />}
                    onClick={() => setShowAnteForm(true)}
                >
                    Agregar antecedente
                </Button>
            )}

            {/* ─── Sección 5: Alergias ─── */}
            <HSeparator />
            <SectionHeader label="Alergias" />

            <Grid templateColumns="1fr 1fr" gap="10px">
                <FormControl>
                    <FormLabel fontSize="xs" fontWeight="600" color={labelColor} mb="6px">Alergia</FormLabel>
                    <Input
                        size="sm"
                        borderRadius="7px"
                        placeholder="Medicamento, alimento, látex..."
                        value={alergiaDesc}
                        onChange={e => setAlergiaDesc(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlergia(); } }}
                    />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="xs" fontWeight="600" color={labelColor} mb="6px">
                        Reacción <Opt />
                    </FormLabel>
                    <Input
                        size="sm"
                        borderRadius="7px"
                        placeholder="Anafilaxia, urticaria..."
                        value={alergiaReaccion}
                        onChange={e => setAlergiaReaccion(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlergia(); } }}
                    />
                </FormControl>
            </Grid>

            <Button
                size="sm"
                variant="outline"
                colorScheme="orange"
                leftIcon={<Icon as={MdAdd} />}
                onClick={addAlergia}
                isDisabled={!alergiaDesc.trim()}
                alignSelf="flex-start"
            >
                Agregar alergia
            </Button>

            {alergias.length > 0 && (
                <Flex flexWrap="wrap" gap="6px">
                    {alergias.map((a, idx) => (
                        <Tag key={idx} size="sm" colorScheme="orange" borderRadius="full">
                            <TagLabel>
                                {a.descripcion}{a.reaccion ? ` — ${a.reaccion}` : ''}
                            </TagLabel>
                            <TagCloseButton onClick={() => removeAlergia(idx)} />
                        </Tag>
                    ))}
                </Flex>
            )}

            </Flex>
            )}
        </CustomDrawer>
    );
};

export default NewPatientDrawer;
